/**
 * BibleDashboard - Enhanced Bible Home dashboard with modern UI
 * Features: Today's verse, Continue reading, Active plans, Progress, Quick actions
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuthStore } from '@/lib/store';
import { cn } from '@/lib/utils';

// UI Components
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';

// Icons
import {
  BookOpen, Calendar, Trophy, Flame, BookMarked, PenLine,
  Highlighter, Bookmark, ChevronRight, Play, Clock, Target,
  TrendingUp, Award, ArrowRight, Sparkles, Heart, Users,
  CheckCircle2, Star, Zap
} from 'lucide-react';

// ============== Today's Verse Card ==============
const TodaysVerseCard = () => {
  const { data: verse, isLoading } = useQuery({
    queryKey: ['verse-of-day'],
    queryFn: () => api.get('/bible/verse-of-the-day').then(r => r.data),
  });

  if (isLoading) {
    return (
      <Card className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 text-white border-0 shadow-xl">
        <CardContent className="p-6">
          <Skeleton className="h-4 w-24 bg-white/20 mb-4" />
          <Skeleton className="h-20 w-full bg-white/20" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 text-white border-0 shadow-xl overflow-hidden relative">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
      
      <CardContent className="p-6 relative">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="h-5 w-5 text-yellow-300" />
          <span className="text-sm font-medium text-white/90">Verse of the Day</span>
        </div>
        
        <blockquote className="text-lg sm:text-xl font-medium leading-relaxed mb-4">
          "{verse?.text}"
        </blockquote>
        
        <div className="flex items-center justify-between">
          <p className="text-white/80 text-sm">
            — {verse?.book} {verse?.chapter}:{verse?.verse}
          </p>
          <Button 
            size="sm" 
            variant="secondary" 
            className="bg-white/20 hover:bg-white/30 text-white border-0"
            onClick={() => {
              if (navigator.share) {
                navigator.share({
                  title: 'Verse of the Day',
                  text: `"${verse?.text}" — ${verse?.book} ${verse?.chapter}:${verse?.verse}`,
                });
              } else {
                navigator.clipboard.writeText(`"${verse?.text}" — ${verse?.book} ${verse?.chapter}:${verse?.verse}`);
                toast.success('Verse copied to clipboard!');
              }
            }}
          >
            Share
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ============== Continue Reading Card ==============
const ContinueReadingCard = ({ lastReading }) => {
  const navigate = useNavigate();

  if (!lastReading) {
    return (
      <Card className="hover:shadow-lg transition-all cursor-pointer group" onClick={() => navigate('/bible')}>
        <CardContent className="p-6 flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white shadow-lg">
            <BookOpen className="h-7 w-7" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-slate-900">Start Reading</h3>
            <p className="text-sm text-slate-500">Begin your Bible journey today</p>
          </div>
          <ChevronRight className="h-5 w-5 text-slate-400 group-hover:translate-x-1 transition-transform" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="hover:shadow-lg transition-all cursor-pointer group" onClick={() => navigate(`/bible/${lastReading.book}/${lastReading.chapter}`)}>
      <CardContent className="p-6 flex items-center gap-4">
        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white shadow-lg">
          <BookOpen className="h-7 w-7" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-slate-900">Continue Reading</h3>
          <p className="text-sm text-slate-500">
            {lastReading.book} {lastReading.chapter}
          </p>
        </div>
        <ChevronRight className="h-5 w-5 text-slate-400 group-hover:translate-x-1 transition-transform" />
      </CardContent>
    </Card>
  );
};

// ============== Active Plan Card ==============
const ActivePlanCard = ({ plan, onContinue }) => {
  const progressPercent = plan.completion_percent || 0;
  
  return (
    <Card className="hover:shadow-lg transition-all overflow-hidden">
      <div className="h-2 bg-gradient-to-r from-emerald-500 to-teal-500" style={{ width: `${progressPercent}%` }} />
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white shadow-md flex-shrink-0">
            <Calendar className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-900 truncate">{plan.plan_title}</h3>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm text-slate-500">Day {plan.current_day} of {plan.duration_days}</span>
              <Badge variant="outline" className="text-xs">{Math.round(progressPercent)}%</Badge>
            </div>
            <Progress value={progressPercent} className="h-1.5 mt-3" />
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <Button 
            size="sm" 
            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
            onClick={() => onContinue(plan)}
          >
            <Play className="h-4 w-4 mr-1" /> Today's Reading
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ============== Stats Cards ==============
const StatsCard = ({ icon: Icon, label, value, color, subtext }) => (
  <Card className="hover:shadow-md transition-all">
    <CardContent className="p-4 flex items-center gap-3">
      <div className={cn("w-11 h-11 rounded-xl flex items-center justify-center text-white shadow-md", color)}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-2xl font-bold text-slate-900">{value}</div>
        <div className="text-xs text-slate-500">{label}</div>
        {subtext && <div className="text-xs text-emerald-600 font-medium">{subtext}</div>}
      </div>
    </CardContent>
  </Card>
);

// ============== Quick Actions ==============
const QuickActions = ({ navigate, isAuthenticated }) => {
  const actions = [
    { icon: BookOpen, label: 'Read Bible', path: '/bible', color: 'from-blue-500 to-indigo-600' },
    { icon: Calendar, label: 'Reading Plans', path: '/bible', tab: 'plans', color: 'from-emerald-500 to-teal-600' },
    { icon: TrendingUp, label: 'My Progress', path: '/bible', tab: 'progress', color: 'from-purple-500 to-pink-600' },
    { icon: PenLine, label: 'Notes', path: '/bible', tab: 'notes', color: 'from-amber-500 to-orange-600' },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {actions.map((action) => (
        <Card 
          key={action.label}
          className="hover:shadow-lg transition-all cursor-pointer group"
          onClick={() => {
            if (!isAuthenticated && action.tab) {
              toast.info('Please sign in to access this feature');
              navigate('/login');
            } else {
              navigate(action.path);
            }
          }}
        >
          <CardContent className="p-4 text-center">
            <div className={cn(
              "w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center text-white shadow-md bg-gradient-to-br group-hover:scale-110 transition-transform",
              action.color
            )}>
              <action.icon className="h-6 w-6" />
            </div>
            <span className="text-sm font-medium text-slate-700">{action.label}</span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ============== Featured Plans ==============
const FeaturedPlans = ({ plans, onStart }) => {
  if (!plans || plans.length === 0) return null;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-900">Featured Reading Plans</h2>
        <Button variant="ghost" size="sm" className="text-indigo-600">
          View All <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {plans.slice(0, 4).map((plan) => (
          <Card key={plan.id} className="hover:shadow-lg transition-all overflow-hidden group">
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                  <BookMarked className="h-5 w-5 text-indigo-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900 truncate group-hover:text-indigo-600 transition-colors">
                    {plan.title}
                  </h3>
                  <p className="text-sm text-slate-500 mt-0.5">{plan.duration_days} days</p>
                </div>
              </div>
              <p className="text-sm text-slate-600 mt-3 line-clamp-2">{plan.description}</p>
              <div className="mt-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-xs capitalize">{plan.difficulty || 'medium'}</Badge>
                  {plan.subscribers_count > 0 && (
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      <Users className="h-3 w-3" /> {plan.subscribers_count}
                    </span>
                  )}
                </div>
                <Button size="sm" variant="outline" onClick={() => onStart(plan.id)}>
                  Start
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// ============== Main Bible Dashboard ==============
const BibleDashboard = ({ embedded = false }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAuthenticated, user } = useAuthStore();

  // Fetch user stats
  const { data: stats, isLoading: loadingStats } = useQuery({
    queryKey: ['bible-stats'],
    queryFn: () => api.get('/bible/my-stats').then(r => r.data),
    enabled: !!isAuthenticated,
  });

  // Fetch active plans
  const { data: myPlans = [], isLoading: loadingPlans } = useQuery({
    queryKey: ['my-plans'],
    queryFn: () => api.get('/bible/my-plans').then(r => r.data),
    enabled: !!isAuthenticated,
  });

  // Fetch available plans
  const { data: availablePlans = [] } = useQuery({
    queryKey: ['bible-plans'],
    queryFn: () => api.get('/bible/plans').then(r => r.data),
  });

  // Get last reading from localStorage
  const [lastReading, setLastReading] = useState(null);
  useEffect(() => {
    const stored = localStorage.getItem('bible_last_reading');
    if (stored) {
      try {
        setLastReading(JSON.parse(stored));
      } catch (e) {}
    }
  }, []);

  // Start plan mutation
  const startPlanMutation = useMutation({
    mutationFn: (planId) => api.post(`/bible/plans/${planId}/start`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries(['my-plans']);
      toast.success('Reading plan started!');
    },
    onError: (err) => {
      const msg = err.response?.data?.detail || 'Failed to start plan';
      toast.error(msg);
    },
  });

  const handleContinuePlan = (plan) => {
    navigate('/bible', { state: { planId: plan.plan_id, day: plan.current_day } });
  };

  const handleStartPlan = (planId) => {
    if (!isAuthenticated) {
      toast.info('Please sign in to start a reading plan');
      navigate('/login');
      return;
    }
    startPlanMutation.mutate(planId);
  };

  const activePlans = myPlans.filter(p => p.status === 'active');

  // Compact version for embedding
  if (embedded) {
    return (
      <div className="space-y-4">
        <TodaysVerseCard />
        {activePlans.length > 0 && (
          <ActivePlanCard plan={activePlans[0]} onContinue={handleContinuePlan} />
        )}
        <ContinueReadingCard lastReading={lastReading} />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Today's Verse */}
      <TodaysVerseCard />

      {/* Stats Row - Only for authenticated users */}
      {isAuthenticated && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatsCard 
            icon={Flame} 
            label="Day Streak" 
            value={stats?.current_streak || 0}
            color="bg-gradient-to-br from-orange-500 to-red-500"
            subtext={stats?.current_streak > 0 ? 'Keep it up!' : null}
          />
          <StatsCard 
            icon={Trophy} 
            label="Best Streak" 
            value={stats?.longest_streak || 0}
            color="bg-gradient-to-br from-amber-500 to-yellow-500"
          />
          <StatsCard 
            icon={Highlighter} 
            label="Highlights" 
            value={stats?.highlights_count || 0}
            color="bg-gradient-to-br from-emerald-500 to-green-500"
          />
          <StatsCard 
            icon={PenLine} 
            label="Notes" 
            value={stats?.notes_count || 0}
            color="bg-gradient-to-br from-blue-500 to-indigo-500"
          />
        </div>
      )}

      {/* Quick Actions */}
      <QuickActions navigate={navigate} isAuthenticated={isAuthenticated} />

      {/* Active Plans */}
      {isAuthenticated && activePlans.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Your Active Plans</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {activePlans.map((plan) => (
              <ActivePlanCard key={plan.id} plan={plan} onContinue={handleContinuePlan} />
            ))}
          </div>
        </div>
      )}

      {/* Continue Reading */}
      <ContinueReadingCard lastReading={lastReading} />

      {/* Featured Plans */}
      <FeaturedPlans plans={availablePlans} onStart={handleStartPlan} />

      {/* Sign in prompt for non-authenticated users */}
      {!isAuthenticated && (
        <Card className="bg-gradient-to-r from-slate-50 to-slate-100 border-slate-200">
          <CardContent className="p-6 flex flex-col sm:flex-row items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-indigo-100 flex items-center justify-center">
              <Star className="h-7 w-7 text-indigo-600" />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <h3 className="font-semibold text-slate-900">Track Your Progress</h3>
              <p className="text-sm text-slate-600 mt-1">
                Sign in to save your reading progress, notes, highlights, and join reading plans.
              </p>
            </div>
            <Button onClick={() => navigate('/login')} className="bg-indigo-600 hover:bg-indigo-700">
              Sign In <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BibleDashboard;
